import { formatCredits, type LedgerEntry } from '@/api/billing'

/** Human labels for ledger entry types — the raw codes are for the database. */
const ENTRY_LABEL: Record<string, string> = {
  trial_grant: 'Welcome credits',
  monthly_grant: 'Monthly plan credits',
  purchase: 'Credit purchase',
  purchase_bonus: 'Bonus credits',
  admin_adjustment: 'Adjustment',
  debit_llm: 'AI usage',
  debit_download: 'Runtime download',
  debit_runtime_user: 'Runtime user seat',
  debit_registration_fee: 'App registration fee',
  refund: 'Refund',
  expiry: 'Credits expired',
  transfer_in: 'Transfer in',
  transfer_out: 'Transfer out',
}

export default function TransactionsTable({
  entries, title = 'Credit history', emptyText = 'No credit activity yet.',
}: {
  entries: LedgerEntry[]
  title?: string
  emptyText?: string
}) {
  return (
    <div className="card" style={{ padding: '1.25rem' }}>
      <h3 style={{ margin: '0 0 0.875rem', fontSize: '1.0625rem' }}>{title}</h3>
      {!entries.length ? (
        <p style={{ color: 'var(--color-text-secondary)', fontSize: '0.9375rem', margin: 0 }}>
          {emptyText}
        </p>
      ) : (
        <div style={{ overflowX: 'auto' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.9375rem' }}>
            <thead>
              <tr style={{ textAlign: 'left', color: 'var(--color-text-secondary)', fontSize: '0.8125rem' }}>
                <th style={{ padding: '0.5rem 0.5rem 0.5rem 0' }}>Date</th>
                <th style={{ padding: '0.5rem' }}>Activity</th>
                <th style={{ padding: '0.5rem', textAlign: 'right' }}>Credits</th>
                <th style={{ padding: '0.5rem 0 0.5rem 0.5rem', textAlign: 'right' }}>Balance</th>
              </tr>
            </thead>
            <tbody>
              {entries.map((e) => (
                <tr key={e.id} style={{ borderTop: '1px solid var(--color-border)' }}>
                  <td style={{ padding: '0.5rem 0.5rem 0.5rem 0', whiteSpace: 'nowrap', color: 'var(--color-text-secondary)' }}>
                    {e.created_at ? new Date(e.created_at).toLocaleString(undefined, {
                      day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit',
                    }) : '—'}
                  </td>
                  <td style={{ padding: '0.5rem' }}>
                    {ENTRY_LABEL[e.entry_type] || e.entry_type}
                    {e.description && (
                      <div style={{ fontSize: '0.8125rem', color: 'var(--color-text-secondary)' }}>
                        {e.description}
                      </div>
                    )}
                  </td>
                  <td style={{
                    padding: '0.5rem', textAlign: 'right', fontVariantNumeric: 'tabular-nums',
                    color: e.amount >= 0 ? 'var(--color-success, #2e9e5b)' : 'var(--color-text-primary)',
                  }}>
                    {e.amount >= 0 ? '+' : '−'}{formatCredits(Math.abs(e.amount))}
                  </td>
                  <td style={{
                    padding: '0.5rem 0 0.5rem 0.5rem', textAlign: 'right',
                    fontVariantNumeric: 'tabular-nums', color: 'var(--color-text-secondary)',
                  }}>
                    {formatCredits(e.balance_after)}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  )
}
